import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { ReleasesService } from '../../../modules/user/releases/releases.service';
import { delay, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { TrackDetailsComponent } from '../track-details/track-details.component';
import { MatDialog } from '@angular/material/dialog';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { translate } from '@ngneat/transloco';
import { cloneDeep } from 'lodash-es';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { FuseUtilsService } from '@fuse/services/utils';
import { UtilService } from 'app/shared/util.service';
import { PlatformService } from 'app/core/platform/platform.service';
import { PlatformSettings } from 'app/core/platform/platform.types';
import { Alert } from 'app/shared/shared.types';
import { fuseAnimations } from '@fuse/animations';
import { trackByUuid } from 'app/utils';
import { ComponentCanDeactivate } from 'app/core/auth/guards/pendingChanges.guard';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { AuthService } from 'app/core/auth/auth.service';
import { NewArtistModalComponent } from 'app/shared/components/new-artist-modal/new-artist-modal';
import { isEqual } from 'lodash-es';
import { ReleaseSubscription } from 'app/modules/user/payments/payments.types';
import { Release, Artist, Label, Genre, Store, AudioFormat, ParticipantRole, Track, TrackParticipant, ReleaseParticipant, Language } from 'app/modules/user/releases/releases.types';
import { ReleaseApprovalService } from 'app/modules/partner/release-approval/release-approval.service';
import { Service } from 'app/modules/user/services/services.types';
import { ServicesService } from 'app/modules/user/services/services.service';
import { AudioPlayerService } from 'app/shared/components/audio-player/audio-player.service';

@Component({
    selector: 'app-new-release-form',
    templateUrl: './new-release-form.component.html',
    styleUrls: ['./new-release-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    animations: fuseAnimations
})
export class NewReleaseFormComponent implements OnInit, OnDestroy, ComponentCanDeactivate {
    @ViewChild('artistInput') artistInput: ElementRef<HTMLInputElement>;
    @ViewChild('featArtistInput') featArtistInput: ElementRef<HTMLInputElement>;
    @ViewChild('allStoresCheckbox') allStoresCheckbox: ElementRef<HTMLInputElement>;

    user: User;
    releaseForm: FormGroup;
    platform: PlatformSettings;
    release: Release;
    artists: Artist[];
    filteredArtists: Artist[];
    labels: Label[];
    languages: Language[];
    genres: Genre[];
    stores: Store[];
    expressDeliveryService: Service;
    copyrightStartYear = 1980;
    copyrightYears: number[];
    flashMessage: 'success' | 'error' | null = null;
    filteredLabels$: Observable<Label[]>;
    usableSubscriptions$: Observable<ReleaseSubscription[]>;
    newTrackInput: FormControl;
    currentStep = 0;

    alert: Alert = {
        type: 'success',
        message: ''
    };

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(private _formBuilder: FormBuilder,
        private _releasesService: ReleasesService,
        private _releaseApprovalService: ReleaseApprovalService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _matDialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
        private _ngZone: NgZone,
        private _fuseUtilsService: FuseUtilsService,
        public utilService: UtilService,
        public platformService: PlatformService,
        private _userService: UserService,
        private _authService: AuthService,
        private _servicesService: ServicesService,
        public audioPlayerService: AudioPlayerService,
    ) {
    }

    @HostListener('window:beforeunload')
    canDeactivate(): boolean | Observable<boolean> {
        if (this.releaseForm.dirty) {
            let dialogRef = this._fuseConfirmationService.open({
                'title': translate('unsaved-changes-confirmation-header'),
                'message': translate('unsaved-changes-confirmation-message'),
                'icon': {
                    'show': true,
                    'name': 'heroicons_outline:exclamation-triangle',
                    'color': 'warning'
                },
                'actions': {
                    'confirm': {
                        'show': true,
                        'label': translate('save-button'),
                        'color': 'primary'
                    },
                    'cancel': {
                        'show': true,
                        'label': translate('discard-button')
                    }
                },
                'dismissible': false
            });
            let dialogObservable = dialogRef.afterClosed().pipe(switchMap((result) => {
                if (result === "confirmed") {
                    console.log("Saving")
                    let release = this.getCurrentReleaseFromForm();
                    return this.updateRelease(release, false).pipe(map(() => true));
                }
                console.log("Not saving")
                return of(true);
            }));
            return dialogObservable;
        }

        return true;
    }

    ngOnInit(): void {
        this.artists = this._activatedRoute.snapshot.data.artists;
        this.genres = this._activatedRoute.snapshot.data.genres;
        this.stores = this._activatedRoute.snapshot.data.stores;

        this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe(user => {
            this.user = user;
        });

        this.utilService.languages$.pipe(takeUntil(this._unsubscribeAll))
            .subscribe(languages => this.languages = languages);

        this.platformService.platform$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(platform => {
                this.platform = platform
            });

        this._servicesService.getExpressDeliveryServices().pipe(takeUntil(this._unsubscribeAll)).subscribe(services => {
            if (services.length) {
                this.expressDeliveryService = services[0];
            }
        });

        this.releaseForm = this._formBuilder.group({
            metadata: this._formBuilder.group({
                uuid: [''],
                title: ['', Validators.required],
                version: [''],
                description: [null],
                label: [{ title: '' }, Validators.required],
                artists: [[], Validators.required],
                featArtists: [[]],
                language: [null, Validators.required],
                audioLanguage: ['0', Validators.required],
                primaryGenre: ['', Validators.required],
                subGenre: [''],
                releaseDate: [null, [Validators.required, this.releaseDateValidator.bind(this)()]],
                preorderDate: [null],
                previouslyPublished: [false],
                originalReleaseDate: [''],
                ean: [''],
                catalogId: [''],
                copyrightText: ['', Validators.required],
                copyrightYear: ['', Validators.required],
                pText: ['', Validators.required],
                pYear: ['', Validators.required],
                artwork: [{ fileName: '' }, Validators.required],
            }),
            tracks: this._formBuilder.array([], this.trackListValidator.bind(this)()),
            newTrackInput: [''],
            storesStep: this._formBuilder.group({
                geoRestrictionStyle: ['none'],
                countryWhitelist: [[]],
                countryBlacklist: [[]],
                publishAtFutureStores: [false],
                stores: this._formBuilder.group([])
            }, { validators: [this.storesValidator.bind(this)()] }),
            reviewStep: this._formBuilder.group({
                subscription: ['', this.user.role == 'user' ? Validators.required : undefined]
            }, { validators: [this.singleValidator, this.composerValidator, this.duplicateTrackValidator] }),
        });
        this.setValidators();

        window['releaseForm'] = this.releaseForm; //TODO remove

        const currentYear = new Date().getFullYear();
        this.copyrightYears = new Array<number>(currentYear - this.copyrightStartYear);
        for (let i = 0; i <= currentYear - this.copyrightStartYear; i++) {
            this.copyrightYears[i] = i + this.copyrightStartYear;
        }
        this.copyrightYears = this.copyrightYears.reverse();

        this._releasesService.release$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((release) => {
                this.release = release;
                if (this.release.audioLanguage === null) {
                    this.release.audioLanguage = '0';
                }
                this.releaseForm.get('metadata').patchValue(release, { emitEvent: true });
                this.releaseForm.get('storesStep').patchValue(release);
                this.releaseForm.get('reviewStep').patchValue(release);

                this.releaseForm.get('metadata.previouslyPublished').setValue(!!release.ean || !!release.originalReleaseDate);

                for (let track of release.tracks) {
                    if (track.audioFiles && track.audioFiles.length > 0) {
                        track.audioFile = track.audioFiles.find(f => f.type === AudioFormat.Master);
                    }
                }

                let artists = this.getReleaseArtistsByRole(ParticipantRole.MainArtist);
                let featArtists = this.getReleaseArtistsByRole(ParticipantRole.FeaturedArtist);
                this.releaseForm.get('metadata.artists').setValue(artists);
                this.releaseForm.get('metadata.featArtists').setValue(featArtists);

                if (release.releaseDate) {
                    this.releaseForm.get('metadata.releaseDate').setValue((release.releaseDate as unknown as string)?.split('T')[0]);
                } else {
                    let defaultReleaseDate = new Date();
                    defaultReleaseDate.setDate(defaultReleaseDate.getDate() + 5);
                    let defaultReleaseDateString = defaultReleaseDate.toISOString().split('T')[0];
                    let todayString = new Date().toISOString().split('T')[0];

                    if (this.user.role === 'label') {
                        defaultReleaseDateString = todayString;
                    }
                    this.releaseForm.get('metadata.releaseDate').setValue(defaultReleaseDateString);
                    this.releaseForm.get('metadata.preorderDate').setValue(todayString);
                }

                if (release.originalReleaseDate) {
                    this.releaseForm.get('metadata.originalReleaseDate').setValue((release.originalReleaseDate as unknown as string)?.split('T')[0]);
                }

                if (release.preorderDate) {
                    this.releaseForm.get('metadata.preorderDate').setValue((release.preorderDate as unknown as string)?.split('T')[0]);
                }

                if (!release.language) {
                    this.releaseForm.get("metadata.language").setValue(this.languages.find(l => l.alpha3 === "eng"));
                }

                if (release.countryWhitelist?.length) {
                    this.releaseForm.get('storesStep').get('geoRestrictionStyle').setValue('whitelist');
                } else if (release.countryBlacklist?.length) {
                    this.releaseForm.get('storesStep').get('geoRestrictionStyle').setValue('blacklist');
                }

                /*const tracksArray = this.releaseForm.get('tracks') as FormArray;

                this.release?.tracks?.forEach((track) => {
                    const trackControl = new FormControl(track);
                    tracksArray.controls.push(trackControl);
                });*/
                window['release'] = release; //TODO remove
                window['releaseForm'] = this.releaseForm;
                window['component'] = this;

                if (release === null) {
                    this._router.navigate(['../'], { relativeTo: this._activatedRoute });
                }
                this.releaseForm.updateValueAndValidity();
                this.conditionallyDisableForm();
                this._changeDetectorRef.markForCheck();
            });

        this._releasesService.labels$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((labels: Label[]) => {
                this.labels = labels;
                this._changeDetectorRef.markForCheck();
            });

        this.filteredLabels$ = this.releaseForm.get('metadata').get('label').valueChanges
            .pipe(
                startWith(''),
                takeUntil(this._unsubscribeAll),
                map(value => this._filterLabels(this.releaseForm.get('metadata').get('label').value))
            );

        const storesFormGroup = this.releaseForm.get('storesStep').get('stores') as FormGroup;
        this.stores = this.stores.sort((a, b) => a.title.localeCompare(b.title));
        this.stores.forEach((store) => {
            const storeControl = new FormControl(this.release?.stores?.find(s => s.uuid === store.uuid) !== undefined);
            storesFormGroup.addControl(store.uuid, storeControl, { emitEvent: false });
        });

        this._releasesService.artists$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((artists: Artist[]) => {
                this.artists = artists;
                this.filterArtists();
                this.conditionallyDisableForm();
                this._changeDetectorRef.markForCheck();
            });

        this.newTrackInput = (this.releaseForm.get('newTrackInput') as FormControl);
        this.newTrackInput.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((track: Track) => {
            if (!track) {
                return;
            }

            if (!this.release.tracks) {
                this.release.tracks = [];
            }

            track.audioFile = track.audioFiles.find(f => f.type === AudioFormat.Master);
            this.release.tracks.push(track);
            this.releaseForm.get('tracks').updateValueAndValidity();
            this.releaseForm.markAsDirty();
            this._changeDetectorRef.markForCheck();
        });

        if (history.state?.step) {
            this.currentStep = history.state.step;
        }

        this._activatedRoute.queryParamMap.pipe(
            takeUntil(this._unsubscribeAll),
        ).subscribe(queryParams => {
            if (queryParams.has('step')) {
                this.currentStep = +queryParams.get('step');
                if (this.currentStep === 3) {
                    this.usableSubscriptions$ = this._releasesService.getUsableSubscriptions(this.getCurrentReleaseFromForm());

                    if (queryParams.get('subscriptionUuid')) {
                        this.usableSubscriptions$.pipe(take(1)).subscribe(subscriptions => {
                            let subscription = subscriptions.find(s => s.uuid === queryParams.get('subscriptionUuid'));
                            if (subscription) {
                                this.releaseForm.get('reviewStep').get('subscription').setValue(subscription);
                            }
                        });
                    }
                }

                // Remove query params
                //TODO: Breaks back button
                /* this._router.navigate([], {
                    queryParams: {
                        'step': null,
                    },
                    queryParamsHandling: 'merge'
                }) */
            }
        });

        this.conditionallyDisableForm();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    isReleaseEditable() {
        return this.user.role === "admin" || this.user.role === "partner" || this.release.status === "draft" || (this.release.status === "rejected" && !this.release.permanentlyRejected);
    }

    conditionallyDisableForm() {
        if (!this.release) {
            return;
        }

        if (!this.isReleaseEditable()) {
            this.releaseForm.disable();
        }

        if (!["admin", "partner"].includes(this.user.role)) {
            switch (this.release.status) {
                /* case "published": {
                    this.alert = { type: "info", message: "release-form-disabled-because-published" };
                    return;
                } */
                case "pending-approval": {
                    this.alert = { type: "info", message: "release-form-disabled-because-waiting-for-approval" }
                    return;
                }
            }
        }

        if (this.release.permanentlyRejected) {
            this.alert = { type: "error", message: "release-form-disabled-because-permanently-rejected" }
            return;
        }
    }

    setValidators() {
        const eanControl = this.releaseForm.get('metadata.ean');
        const orignalReleaseDateControl = this.releaseForm.get('metadata.originalReleaseDate');

        this.releaseForm.get('metadata.previouslyPublished').valueChanges
            .subscribe(previouslyPublished => {
                if (previouslyPublished) {
                    eanControl.addValidators(this.eanValidator);
                    orignalReleaseDateControl.addValidators(Validators.required)
                } else {
                    eanControl.clearValidators();
                    orignalReleaseDateControl.clearValidators();
                }
                eanControl.updateValueAndValidity();
                orignalReleaseDateControl.updateValueAndValidity();
            });
    }

    releaseDateValidator(): ValidatorFn {
        return (control?: AbstractControl | undefined): ValidationErrors | null => {
            let releaseDate = control.value;
            if (this.user.role === 'label' || this.user.role === "admin" || this.user.role === "partner") {
                return null;
            }

            if (!releaseDate) {
                return null;
            }

            let today = new Date();
            today.setHours(0, 0, 0, 0);
            let releaseDateDate = new Date(releaseDate);
            releaseDateDate.setHours(0, 0, 0, 0);
            if (releaseDateDate < today) {
                return { pastReleaseDate: true };
            }

            if (this.requiresExpressDelivery() && !this.expressDeliveryService) {
                return { releaseDateTooEarly: true };
            }

            /* let defaultReleaseDate = new Date();
            defaultReleaseDate.setHours(0, 0, 0, 0);
            defaultReleaseDate.setDate(defaultReleaseDate.getDate() + 5);

             if (releaseDateDate < defaultReleaseDate) {
                return { tooSoonReleaseDate: true };
            } */

            return null;
        };
    }

    requiresExpressDelivery() {
        if (this.user.role === 'label' || this.user.role === "admin" || this.user.role === "partner") {
            return false;
        }

        //If release date is less than 5 days from now, express delivery is required
        let releaseDate = new Date(this.releaseForm.get('metadata.releaseDate').value);
        let today = new Date();
        today.setHours(0, 0, 0, 0);
        today.setDate(today.getDate() + 5);
        return releaseDate < today;
    }

    getEarliestFreeReleaseDate() {
        let today = new Date();
        today.setHours(0, 0, 0, 0);
        today.setDate(today.getDate() + 5);
        return today;
    }

    trackListValidator(): ValidatorFn {
        return (control?: AbstractControl | undefined): ValidationErrors | null => {
            if (!this.release?.tracks?.length) {
                return { noTracks: true };
            }

            if (this.release?.tracks?.find(track => !this.validateTrack(track))) {
                return { incompleteTrack: true };
            }

            return null;
        };
    }

    eanValidator(): ValidatorFn {
        return (control?: AbstractControl | undefined): ValidationErrors | null => {
            if (!control?.value) {
                return null;
            }

            if (!this.utilService.isValidBarcode(control.value)) {
                return { invalidEan: true };
            }

            return null;
        }
    }

    storesValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const noStoresError = { noStores: true };
            if (!this.releaseForm) {
                return !this.release?.stores?.length ? noStoresError : null;
            }

            const releaseFormData = this.releaseForm.getRawValue();
            const stores = releaseFormData['storesStep']['stores'];
            let enabledStoreFound = false;
            for (const store in stores) {
                if (!stores.hasOwnProperty(store)) {
                    return null;
                }
                if (stores[store] === true) {
                    enabledStoreFound = true;
                }
            }
            return enabledStoreFound ? null : noStoresError;
        };
    }

    singleValidator = (control: FormControl): ValidationErrors | null => {
        if (!this.release?.tracks || this.release.tracks.length !== 1) {
            return null;
        }

        let track = this.release.tracks[0];
        let release = this.release;

        if (track.title !== release.title) {
            return { singleTitleMismatch: true };
        }

        if ((track.version ?? "") !== (release.version ?? "")) {
            return { singleVersionMismatch: true };
        }

        if (track.primaryGenre?.uuid !== release.primaryGenre?.uuid || track.subGenre?.uuid !== release.subGenre?.uuid) {
            return { singleGenreMismatch: true };
        }

        let releaseMainArtists = release.participants.filter(p => p.role === ParticipantRole.MainArtist).map(a => a.participant.uuid);
        let trackMainArtists = track.participants.filter(p => p.role === ParticipantRole.MainArtist).map(a => a.participant.uuid);
        let releaseFeatArtists = release.participants.filter(p => p.role === ParticipantRole.FeaturedArtist).map(a => a.participant.uuid);
        let trackFeatArtists = track.participants.filter(p => p.role === ParticipantRole.FeaturedArtist).map(a => a.participant.uuid);

        if (!isEqual(releaseMainArtists, trackMainArtists) || !isEqual(releaseFeatArtists, trackFeatArtists)) {
            return { singleArtistMismatch: true };
        }

        return null;
    }

    composerValidator = (control: FormControl): ValidationErrors | null => {
        if (!this.release?.tracks) {
            return null;
        }

        for (let track of this.release.tracks) {
            if (track.primaryGenre?.title === "Classical") {
                let composers = track.participants.filter(p => p.role === "Composer");
                if (!composers.length) {
                    return { noComposer: true };
                }
            }
        }

        return null;
    }

    duplicateTrackValidator = (control: FormControl): ValidationErrors | null => {
        if (!this.release?.tracks) {
            return null;
        }

        let trackTitles = this.release.tracks.map(t => t.title + (t.version?.length ? (" (" + t.version + ")") : ""));
        let duplicateTitles = trackTitles.filter((title, index) => trackTitles.indexOf(title) !== index);
        if (duplicateTitles.length) {
            return { duplicateTracks: true };
        }

        return null;
    }

    futureStoresChange(checked: boolean): void {
        const storesFormGroup = this.releaseForm.get('storesStep') as FormGroup;
        const control = storesFormGroup.get('publishAtFutureStores');
        control.setValue(checked, { emitEvent: false });
        control.markAsDirty();

        this.releaseForm.updateValueAndValidity();
        this._changeDetectorRef.markForCheck();
    }

    allStoresChange(checked: boolean): void {
        const storesFormGroup = this.releaseForm.get('storesStep').get('stores') as FormGroup;
        Object.keys(storesFormGroup.controls).forEach((key) => {
            const control = storesFormGroup.get(key);
            control.setValue(checked, { emitEvent: false });
            control.markAsDirty();
        });
        this.releaseForm.updateValueAndValidity();
        this._changeDetectorRef.markForCheck();
    }

    isStoreDisallowed(store: Store): boolean {
        let label = this.releaseForm.get('metadata.label').value as Label;
        if (typeof label !== "object") {
            return true;
        }
        if (store.title === "Beatport" && !label?.beatportPermission) {
            return true;
        }

        return false;
    }

    showFlashMessage(type: 'success' | 'error'): void {
        this.flashMessage = type;
        this._changeDetectorRef.markForCheck();

        // Hide it after 3 seconds
        setTimeout(() => {
            this.flashMessage = null;
            this._changeDetectorRef.markForCheck();
        }, 3000);
    }

    updateRelease(release: Release, publish?: boolean): Observable<Release> {
        //create a copy of the release to avoid changing the original object
        release = JSON.parse(JSON.stringify(release));
        for (let track of release.tracks) {
            delete track.audioFile;
        }

        //Add UTC timezone to release date
        release.releaseDate = new Date(this.releaseForm.get('metadata.releaseDate').value + "T00:00:00.000Z");
        release.originalReleaseDate = new Date(this.releaseForm.get('metadata.originalReleaseDate').value + "T00:00:00.000Z");
        release.preorderDate = new Date(this.releaseForm.get('metadata.preorderDate').value + "T00:00:00.000Z");

        return this._releasesService.updateRelease(release, publish);
    }

    getReleaseArtistsByRole(role: ParticipantRole) {
        return this.release?.participants?.filter(participant => participant.role === role)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(participant => participant.participant) ?? [];
    }

    filterArtists(event?): void {
        this.filteredArtists = this.artists?.filter(artist =>
            (event ? artist.title.toLowerCase().includes(event.target.value.toLowerCase()) : true)
            && !this.releaseForm.get("metadata.artists").value.map(a => a.uuid).includes(artist.uuid)
            && !this.releaseForm.get("metadata.featArtists").value.map(a => a.uuid).includes(artist.uuid)
        ).sort((a, b) => a.title > b.title ? 1 : -1);

        if (this.filteredArtists?.length > 25) {
            this.filteredArtists = this.filteredArtists.slice(0, 25);
        }
    }

    filterArtistsInputKeyDown(event, key: "artists" | "featArtists"): void {
        if ((event.key !== 'Enter' && event.key !== 'Tab') || !event.target.value) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.onArtistBlur(event, key);
    }

    autocompleteMouseDown(event) {
        //This function is needed to prevent the blur event from firing when the user clicks on the autocomplete options
        event.preventDefault();
        event.stopPropagation();
    }

    onArtistBlur(event, key: "artists" | "featArtists"): void {
        if (!event.target.value) {
            return;
        }

        let artists: Artist[] = [...this.releaseForm.get('metadata').get("artists").value, ...this.releaseForm.get('metadata').get("featArtists").value];
        const enteredArtist = event.target.value;
        const isArtistApplied = artists.find(artist => artist.title.toLowerCase() === enteredArtist.toLowerCase());
        if (isArtistApplied) {
            event.target.value = '';
            return;
        }

        const artist = this.artists.find(a => a.title.toLowerCase() === enteredArtist.toLowerCase());
        if (!artist) {
            this.createArtist(enteredArtist, this.release.user.uuid).subscribe((response) => {
                this.addArtist(response, key);
            });
            event.target.value = '';
            return;
        }

        if (!isArtistApplied) {
            this.addArtist(artist, key);
            event.target.value = '';
        }
    }

    createArtist(title: string, userUuid?: string) {
        return this._matDialog.open(NewArtistModalComponent, {
            autoFocus: true,
            data: {
                title: title,
                userUuid: userUuid
            }
        }).afterClosed();
        //return this._releasesService.createArtist(artist);
    }

    getLabelName(inputLabel: Label): string {
        return this.labels?.find(label => label.uuid === inputLabel?.uuid)?.title || '';
    }

    anyLabelHasLogo(): boolean {
        return this.labels?.some(label => label.logo?.fileName);
    }

    getGenreNames(primaryGenre: Genre, subGenre?: Genre): string {
        let genres: Genre[] = [];
        if (primaryGenre) {
            genres.push(primaryGenre);
        }

        if (subGenre) {
            genres.push(subGenre);
        }
        return genres.map(g => g.title).join(', ');
    }

    getArtistNames(artists: Artist[]): string {
        return artists?.map(participant => participant?.title).join(', ');
    }

    getTrackArtistNames(artists: TrackParticipant[]): string {
        let mainArtists = artists?.filter(participant => participant.role === ParticipantRole.MainArtist).sort((a, b) => a.sortOrder - b.sortOrder).map(participant => participant.participant?.title);
        let featuredArtists = artists?.filter(participant => participant.role === ParticipantRole.FeaturedArtist).sort((a, b) => a.sortOrder - b.sortOrder).map(participant => participant.participant?.title);
        return mainArtists?.join(', ') + (featuredArtists?.length ? ' (feat. ' + featuredArtists?.join(', ') + ')' : '');
    }

    getOtherTrackParticipants(artists: TrackParticipant[]): string {
        return artists?.filter(participant => participant.participant && participant.role !== ParticipantRole.MainArtist && participant.role !== ParticipantRole.FeaturedArtist)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(participant => participant.participant?.title)
            .join(', ');
    }

    getStoreName(storeId: string): string {
        return this.stores?.find(store => store.uuid === storeId)?.title || storeId;
    }

    getStoreNames(): string {
        const storeKeys = Object.keys(this.releaseForm.get('storesStep.stores')?.value);
        const activeStoreIds = storeKeys?.filter(storeId => this.releaseForm.get('storesStep.stores.' + storeId)?.value);
        if (!activeStoreIds || !activeStoreIds.length) {
            return translate('no-stores-selected');
        }
        return activeStoreIds?.map(storeId => this.getStoreName(storeId))?.join(', ') ?? translate('no-stores-selected');
    }

    addArtistFromAutocomplete(event: MatAutocompleteSelectedEvent, key: "artists" | "featArtists"): void {
        let artists: Artist[] = this.releaseForm.get('metadata').get(key).value;
        const enteredArtist = event.option.value.title;
        const isArtistApplied = artists.find(artist => artist.title === enteredArtist);
        if (isArtistApplied) {
            return;
        }

        this.addArtist(event.option.value, key);
        if (key === 'artists') {
            this.artistInput.nativeElement.value = '';
        } else {
            this.featArtistInput.nativeElement.value = '';
        }
    }


    removeArtist(artist: Artist, key: "artists" | "featArtists"): void {
        const artists = this.releaseForm.get('metadata').get(key);
        const index = artists.value.indexOf(artist);
        if (index >= 0) {
            artists.value.splice(index, 1);
            artists.updateValueAndValidity();
        }
        this.filterArtists();
        this.releaseForm.markAsDirty();
    }

    addArtist(artist: Artist, key: "artists" | "featArtists"): void {
        if (!artist) {
            return;
        }

        const artists = this.releaseForm.get('metadata').get(key);
        artist.title = (artist.title || '').trim();
        if (artist.title) {
            artists.value.push(artist);
        }
        this.releaseForm.markAsDirty();
        artists.updateValueAndValidity();
        this._changeDetectorRef.markForCheck();
        this.filterArtists();
    }

    trackDropped(event: CdkDragDrop<Track[]>): void {
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        this.releaseForm.markAsDirty();
        this._changeDetectorRef.markForCheck();
    }

    getFormatString(track: Track) {
        if (!track.audioFile) {
            return "";
        }

        let codecDetails = [`${Math.round(track.audioFile.bitRate / 10) / 100} kbps`, `${Math.round(track.audioFile.samplingRate / 10) / 100} kHz`]
        if (track.audioFile.lossless && track.audioFile.bitDepth) {
            codecDetails.push(`${track.audioFile.bitDepth} bit`);
        }

        return `${track.audioFile.codec} (${codecDetails.join(', ')})`;
    }

    submitForm(redirectUrl?: string, submitButtonId?: string): void {
        let release = this.getCurrentReleaseFromForm();
        if (this.user.role === "admin" && redirectUrl) {
            redirectUrl = "/admin/release-approval/" + release.uuid;
        } else if (this.user.role === "partner" && redirectUrl) {
            redirectUrl = "/partner/release-approval/" + release.uuid;
        }

        this.updateRelease(release, submitButtonId === "publish").subscribe({
            next: (updatedRelease) => {
                if (submitButtonId === "publish") {
                    this._userService.setLocalTutorialProgress({ releasePublishCompleted: true });
                }
                if (["admin", "partner"].includes(this.user.role)) {
                    this._releaseApprovalService.getReleaseById(updatedRelease.uuid).subscribe();
                }
                this.showFlashMessage('success');
                this.releaseForm.markAsPristine();
                if (redirectUrl) {
                    console.log('redirect to /' + redirectUrl);
                    this._router.navigate([redirectUrl], { queryParams: { release: release.uuid, express: this.requiresExpressDelivery() } });
                }
            },
            error: (err) => {
                console.log(err);
                this.showFlashMessage('error');
            }
        });

    }

    getCurrentReleaseFromForm(): Release {
        this.releaseForm.get('metadata').updateValueAndValidity();
        this._changeDetectorRef.markForCheck();
        const releaseFormData = this.releaseForm.getRawValue();
        const stores = releaseFormData['storesStep']['stores'];
        let storesArray = [];
        for (const store in stores) {
            if (!stores.hasOwnProperty(store)) {
                return;
            }
            if (stores[store] === true) {
                storesArray.push({ uuid: store });
            }
        }
        let updatedRelease = { ...this.release, ...releaseFormData['metadata'] } as Release;
        updatedRelease.stores = storesArray;
        updatedRelease.publishAtFutureStores = releaseFormData['storesStep']['publishAtFutureStores'];
        if (releaseFormData['storesStep']['geoRestrictionStyle'] === 'whitelist') {
            updatedRelease.countryWhitelist = releaseFormData['storesStep']['countryWhitelist'];
            updatedRelease.countryBlacklist = [];
        } else if (releaseFormData['storesStep']['geoRestrictionStyle'] === 'blacklist') {
            updatedRelease.countryBlacklist = releaseFormData['storesStep']['countryBlacklist'];
            updatedRelease.countryWhitelist = [];
        } else {
            updatedRelease.countryWhitelist = [];
            updatedRelease.countryBlacklist = [];
        }
        updatedRelease.subscription = releaseFormData['reviewStep']['subscription'];

        if (typeof (updatedRelease.label) === "string") {
            updatedRelease.label = { title: updatedRelease.label };
        }

        if (updatedRelease.audioLanguage === '0') {
            updatedRelease.audioLanguage = null;
        }

        let participants: ReleaseParticipant[] = [];
        for (let artist of releaseFormData['metadata'].artists) {
            participants.push({
                participant: artist,
                role: ParticipantRole.MainArtist,
                release_uuid: updatedRelease.uuid,
                artist_uuid: artist.uuid,
                uuid: this.release.participants.find(participant => participant.participant.title === artist.title && participant.role === ParticipantRole.MainArtist)?.uuid
            });
        };
        for (let artist of releaseFormData['metadata'].featArtists) {
            participants.push({
                participant: artist,
                role: ParticipantRole.FeaturedArtist,
                release_uuid: updatedRelease.uuid,
                artist_uuid: artist.uuid,
                uuid: this.release.participants.find(participant => participant.participant.title === artist.title && participant.role === ParticipantRole.FeaturedArtist)?.uuid
            });
        };

        let remainingParticipants = this.release.participants.filter(participant => participant.role !== ParticipantRole.MainArtist && participant.role !== ParticipantRole.FeaturedArtist);
        participants = [...participants, ...remainingParticipants];

        let i = 0;
        for (let participant of participants) {
            participant.sortOrder = i++;
        }

        for (let updatedReleaseKey in updatedRelease) {
            if (updatedRelease.hasOwnProperty(updatedReleaseKey)) {
                if (updatedRelease[updatedReleaseKey] === '') {
                    updatedRelease[updatedReleaseKey] = null;
                }

                //trim all strings
                if (typeof (updatedRelease[updatedReleaseKey]) === 'string') {
                    updatedRelease[updatedReleaseKey] = updatedRelease[updatedReleaseKey].trim();
                }
            }
        }

        updatedRelease.participants = participants;
        delete updatedRelease['artists'];
        delete updatedRelease['featArtists'];

        return updatedRelease;
    }

    openTrackModal(track: Track) {
        const trackIndex = this.release.tracks.findIndex(currentTrack => track === currentTrack)
        if (trackIndex === -1) {
            console.error('Can\'t open modal for track', track.uuid);
            return;
        }

        const fillData = {} as Track;
        fillData.language = track.language ?? this.releaseForm.get('metadata').get('audioLanguage').value ?? '';

        fillData.participants = track.participants ?? [];
        if (fillData.participants.length === 0) {
            let metadata = this.releaseForm.get('metadata');
            let mappingFunction = (artist: Artist, role: string): TrackParticipant => {
                return {
                    participant: artist,
                    role: role,
                    email: artist.email,
                    share: null
                }
            };

            //get previous track
            let previousTrack = this.release.tracks[trackIndex - 1];
            fillData.participants = [...previousTrack?.participants]
            if (!fillData.participants.length) {
                fillData.participants = [
                    ...metadata.get('artists').value.map(artist => mappingFunction(artist, "Main Artist")),
                    ...metadata.get('featArtists').value.map(artist => mappingFunction(artist, "Featured Artist")),
                ];
            } else {
                fillData.participants = fillData.participants.map(participant => {
                    let copy = { ...participant };
                    delete copy.uuid;
                    return copy;
                });
            }
        }

        fillData.primaryGenre = track.primaryGenre ?? undefined;
        if (!fillData.primaryGenre) {
            fillData.primaryGenre = this.releaseForm.get('metadata').get('primaryGenre').value;
        }

        fillData.subGenre = track.subGenre ?? undefined;
        if (!fillData.subGenre) {
            fillData.subGenre = this.releaseForm.get('metadata').get('subGenre').value;
        }


        this._matDialog.open(TrackDetailsComponent, {
            autoFocus: false,
            data: {
                trackIndex: trackIndex,
                track: cloneDeep(track),
                genres: this.genres,
                languages: this.languages,
                artists$: this._releasesService.artists$,
                fillData: fillData,
                cdn: this.platform.cdn,
                user: this.user,
            }
        })
            .afterClosed()
            .subscribe((updatedTrack) => {
                if (!updatedTrack) {
                    return;
                }

                this.release.tracks[trackIndex] = updatedTrack;
                if (updatedTrack.subGenre === '') {
                    this.release.tracks[trackIndex].subGenre = null;
                }

                for (let [i, participant] of updatedTrack.participants.entries()) {
                    this.release.tracks[trackIndex].participants[i].sortOrder = i;
                    let artist = participant.participant;
                    if (!artist.uuid) {
                        /* let existingArtist = this.artists.find(a => a.title === artist.title);
                        if (existingArtist) {
                            this.release.tracks[trackIndex].participants[i].participant.uuid = existingArtist.uuid;
                            this.release.tracks[trackIndex].participants[i].artist_uuid = existingArtist.uuid;
                        } else {
                            artist = this._releasesService.createArtist(artist).subscribe(updatedArtist => {
                                this.release.tracks[trackIndex].participants[i].participant = updatedArtist;
                                this.release.tracks[trackIndex].participants[i].artist_uuid = updatedArtist.uuid;
                            })
                        } */
                    }
                }

                this.releaseForm.markAsDirty();
                this.releaseForm.get('tracks').updateValueAndValidity();
                this._changeDetectorRef.markForCheck();
            });
    }

    playTrack(track: Track) {
        this.audioPlayerService.setRelease(this.release);
        this.audioPlayerService.playTrack(track);
    }

    deleteTrack(track: Track) {
        const i = this.release.tracks.indexOf(track);
        if (i === -1) {
            console.error('Could not find track ' + track.uuid + ' while deleting');
            return;
        }

        this.release.tracks.splice(i, 1);
        this.releaseForm.get('tracks').updateValueAndValidity();
        this.releaseForm.markAsDirty();
        this._changeDetectorRef.markForCheck();
    }

    openDeletionConfirmationModal(track: Track, trackIndex: number) {
        const title = (trackIndex + 1).toString() + '. ' + (track.title ?? translate('untitled-track'));
        const dialogRef = this._fuseConfirmationService.open({
            'title': translate('delete-track-confirmation-header'),
            'message': translate('delete-track-confirmation-message', { title: title }),
            'icon': {
                'show': true,
                'name': 'heroicons_outline:exclamation-triangle',
                'color': 'warn'
            },
            'actions': {
                'confirm': {
                    'show': true,
                    'label': translate('delete-track-confirmation-confirm'),
                    'color': 'warn'
                },
                'cancel': {
                    'show': true,
                    'label': translate('cancel')
                }
            },
            'dismissible': true
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.deleteTrack(track);
            }
        });
    }

    compareByUuid = function (option, value): boolean {
        //TODO this if-statement is a temporary hack, remove after release genres is multiple choice
        if (value && Array.isArray(value)) {
            value = value[0];
        }
        return option.uuid === value?.uuid;
    }

    onStepperSelectionChange($event: StepperSelectionEvent) {
        if (this.releaseForm.dirty && this.isReleaseEditable() && !['admin', 'partner'].includes(this.user.role)) {
            this.submitForm();
        }

        if ($event.selectedIndex === 3) {
            this.usableSubscriptions$ = this._releasesService.getUsableSubscriptions(this.getCurrentReleaseFromForm());
        }

        history.replaceState({
            ...history.state,
            step: $event.selectedIndex
        }, '');
    }

    goToSubscriptions() {
        this.submitForm('/subscriptions');
    }

    validateTrack(track: Track) {
        if (!track.title) {
            return false;
        }

        if (!track.participants || track.participants.length === 0) {
            return false;
        }

        if (!track.primaryGenre) {
            return false;
        }

        if (!track.participants.find(p => p.role === ParticipantRole.MainArtist)) {
            return false;
        }

        return true;
    }

    filterLabels(title: string) {
        return this._filterLabels(title);
    }

    private _filterLabels(value: string | Label): Label[] {
        let filterValue: string;
        if (typeof value === 'string') {
            filterValue = value.toLowerCase();
        } else if (typeof value === 'object') {
            filterValue = value?.title;
        }
        let filteredLabels = this.labels
            ?.filter(label => label.title.toLowerCase().includes(filterValue ?? ''))
            .sort((a, b) => a.title > b.title ? 1 : -1) ?? [];
        if (filteredLabels.length > 25) {
            filteredLabels = filteredLabels.slice(0, 25);
        }
        return filteredLabels;
    }

    get trackByUuid() {
        return trackByUuid;
    }
}
